import type { Engine } from "@babylonjs/core/Engines/engine";
import { Scene } from "@babylonjs/core/scene";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { createTerrain } from "./terrain";
import { createSky } from "./sky";
import { GardenRenderer } from "./garden";
import { PollinatorSwarm } from "./swarm";
import { setupWorld } from "../xr";
import { SimClient } from "../worker/client";
import { AmbientAudio } from "../audio";
import { DEFAULT_CONFIG } from "../sim/index";
import { TextPanel } from "./panel";
import { setSceneErrorSink } from "../diagnostics";

// Side-effect imports required by the tree-shaken ES6 build.
import "@babylonjs/core/Materials/standardMaterial";

const SEED = 1; // shares the Phase 1 ecology seed so terrain and sim agree
const WORLD_SIZE = 72; // must match DEFAULT_CONFIG.worldSize (sim + terrain share the heightmap)
const WATER_LEVEL = 1.1;

/**
 * Phase 2 world: golden-hour light, procedural island terrain + lochs, and
 * comfortable teleport locomotion + aim-and-plant over a stubbed sim.
 */
export function createScene(engine: Engine, canvas: HTMLCanvasElement): Scene {
  const scene = new Scene(engine);
  scene.clearColor = new Color4(0.86, 0.66, 0.42, 1.0); // warm dusk sky

  // Warm dusk haze: adds aerial depth over the larger island and softens the
  // far shoreline into the sky rather than ending on a hard line.
  scene.fogMode = Scene.FOGMODE_EXP2;
  scene.fogColor = new Color3(0.82, 0.62, 0.42);
  scene.fogDensity = 0.011;

  // --- Golden-hour lighting ---
  // Low sun, close to the horizon (raking golden light).
  const sunDir = new Vector3(-0.42, -0.18, 0.89); // direction the light travels
  const sunToward = sunDir.negate(); // direction to the sun (for the sky disc)
  const sun = new DirectionalLight("sun", sunDir, scene);
  sun.position = new Vector3(30, 25, -60);
  sun.intensity = 2.4;
  sun.diffuse = new Color3(1.0, 0.78, 0.5);
  sun.specular = new Color3(0.3, 0.22, 0.14);

  const ambient = new HemisphericLight("ambient", new Vector3(0, 1, 0), scene);
  ambient.intensity = 0.7;
  ambient.diffuse = new Color3(1.0, 0.88, 0.7);
  ambient.groundColor = new Color3(0.3, 0.34, 0.26);

  // --- Sky dome + terrain ---
  createSky(scene, sunToward);
  const terrain = createTerrain(scene, SEED, WORLD_SIZE, WATER_LEVEL);

  // --- Sim worker + instanced growth rendering + pollinators + audio ---
  const sim = new SimClient({ ...DEFAULT_CONFIG, seed: SEED, worldSize: WORLD_SIZE, waterLevel: WATER_LEVEL });
  new GardenRenderer(scene, terrain, sim);
  new PollinatorSwarm(scene, terrain, sim);
  new AmbientAudio(scene);

  // --- Locomotion + planting (rig owns all camera motion) ---
  const start = findStart(terrain);
  setupWorld(scene, canvas, terrain, sim, start);

  // --- In-headset error surface (the DOM overlay is invisible inside XR) ---
  const errorPanel = new TextPanel(scene, "errorPanel", 1.5, 0.75);
  errorPanel.setEnabled(false);
  setSceneErrorSink((source, detail) => {
    errorPanel.setText(
      [`⚠  ${source}`, "", detail.slice(0, 300), "", "take off the headset to see the full log"],
      { bg: "rgba(120,12,12,0.94)", fg: "#ffffff", fontPx: 30 },
    );
    errorPanel.attachToActiveCamera(scene, 1.5);
    errorPanel.setEnabled(true);
  });

  return scene;
}

/** Pick a pleasant, valid starting spot near the island centre. */
function findStart(terrain: { isValidTarget(x: number, z: number): boolean }): { x: number; z: number } {
  for (let r = 0; r < 20; r++) {
    for (const [x, z] of [
      [0, 0],
      [r, 0],
      [-r, 0],
      [0, r],
      [0, -r],
    ] as const) {
      if (terrain.isValidTarget(x, z)) return { x, z };
    }
  }
  return { x: 0, z: 0 };
}
